// --- START EDIT: self-host HDR environment (no external connect); keep CSP minimal ---
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, Environment } from "@react-three/drei";
import { Suspense, useEffect, useRef, useState, useMemo } from "react";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { useLoader } from "@react-three/fiber";
import * as THREE from "three";

type ModelViewerProps = {
  file?: File;
  url?: string;
};

const PRINTER_BED_SIZE = 256; // mm
const GRID_COLOR = "#7E8186"; // slightly softer than before

function WireGrid({
  bedSize,
  divisions = 20,
  color = GRID_COLOR,
  lineWidth = 1,
}: {
  bedSize: number;
  divisions?: number;
  color?: string;
  lineWidth?: number;
}) {
  const grid = useMemo(() => {
    const step = bedSize / divisions;
    const verts: number[] = [];
    for (let i = 0; i <= divisions; i++) {
      const x = -bedSize / 2 + i * step;
      verts.push(x, 0.01, -bedSize / 2, x, 0.01, bedSize / 2);
    }
    for (let i = 0; i <= divisions; i++) {
      const z = -bedSize / 2 + i * step;
      verts.push(-bedSize / 2, 0.01, z, bedSize / 2, 0.01, z);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(verts, 3));
    return geometry;
  }, [bedSize, divisions]);

  return (
    <lineSegments geometry={grid}>
      <lineBasicMaterial color={color} linewidth={lineWidth} depthTest={false} />
    </lineSegments>
  );
}

function PrintingBed({ bedSize }: { bedSize: number }) {
  return (
    <group>
      <WireGrid bedSize={bedSize} divisions={20} />
    </group>
  );
}

function CenteredGroup({
  geometry,
  children,
}: {
  geometry: THREE.BufferGeometry;
  children: React.ReactNode;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const controls = useThree(
    (state) =>
      state.controls as import("three/examples/jsm/controls/OrbitControls").OrbitControls | undefined
  );
  const camera = useThree((state) => state.camera);

  useEffect(() => {
    if (geometry && groupRef.current) {
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
    }
  }, [geometry, controls, camera]);

  return (
    <group ref={groupRef} rotation={[-Math.PI / 2, 0, 0]}>
      {children}
    </group>
  );
}

function STLModel({ url }: { url: string }) {
  const geometry = useLoader(STLLoader, url) as THREE.BufferGeometry;
  return (
    <CenteredGroup geometry={geometry}>
      <mesh geometry={geometry} castShadow>
        <meshStandardMaterial color="#CFCFCF" metalness={0.2} roughness={0.6} />
      </mesh>
    </CenteredGroup>
  );
}

function OBJModel({ url }: { url: string }) {
  const obj = useLoader(OBJLoader, url);
  let geometry: THREE.BufferGeometry | null = null;
  if ((obj as unknown as THREE.Mesh).isMesh) {
    geometry = (obj as unknown as THREE.Mesh).geometry as THREE.BufferGeometry;
  } else if ((obj as THREE.Group).children.length > 0) {
    const meshChild = (obj as THREE.Group).children.find(
      (c) => (c as THREE.Mesh).isMesh
    ) as THREE.Mesh | undefined;
    if (meshChild) geometry = meshChild.geometry as THREE.BufferGeometry;
  }
  return geometry ? (
    <CenteredGroup geometry={geometry}>
      <primitive object={obj} castShadow />
    </CenteredGroup>
  ) : (
    <primitive object={obj} castShadow />
  );
}

const ModelViewer = (props: ModelViewerProps) => {
  const [blobUrl, setBlobUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (props.file) {
      const url = URL.createObjectURL(props.file);
      setBlobUrl(url);
      return () => {
        URL.revokeObjectURL(url);
        setBlobUrl(undefined);
      };
    } else {
      setBlobUrl(undefined);
    }
  }, [props.file]);

  const sourceUrl = props.file ? blobUrl : props.url;
  let extension: string | undefined = undefined;
  if (props.file && props.file.name) {
    extension = props.file.name.split(".").pop()?.toLowerCase();
  } else if (props.url) {
    extension = props.url.split(".").pop()?.toLowerCase();
  }

  return (
    <div className="h-[480px] mt-6 border border-border rounded-xl bg-card shadow-sm md:shadow flex items-center justify-center relative">
      <Canvas
        shadows
        camera={{ position: [0, 250, 330], fov: 45, near: 0.01 }}
        style={{ background: "transparent" }}
      >
        <ambientLight intensity={0.7} />
        <pointLight position={[10, 120, 120]} intensity={0.8} castShadow />
        <Suspense fallback={null}>
          <PrintingBed bedSize={PRINTER_BED_SIZE} />
          {/* Self-hosted environment map to avoid external CSP connect-src allowances */}
          <Environment files="/hdr/empty_warehouse_01_1k.hdr" />
          {sourceUrl && extension === "stl" && <STLModel url={sourceUrl} />}
          {sourceUrl && extension === "obj" && <OBJModel url={sourceUrl} />}
        </Suspense>
        <OrbitControls enablePan enableZoom enableRotate />
      </Canvas>
      {!sourceUrl && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10">
          <span className="text-foreground/85 text-xl">No model loaded</span>
        </div>
      )}
    </div>
  );
};

export default ModelViewer;
// --- END EDIT: self-host HDR environment (no external connect); keep CSP minimal ---